import type { ProductEngagement } from './productEngagementTypes';

type Cell = string | number | null;
const columns = ['section', 'date', 'event', 'platform', 'source', 'contentType', 'imageType', 'stage', 'reason', 'result',
  'id', 'name', 'count', 'users', 'images', 'sessions', 'durationMs', 'avgDurationMs'] as const;
type Row = Partial<Record<typeof columns[number], Cell>>;

function cell(value: Cell | undefined): string {
  const raw = value == null ? '' : String(value);
  // Lens names and usernames are editable; spreadsheets must treat them as text.
  const safe = typeof value === 'string' && /^[\s]*[=+@-]|^[\t\r]/.test(raw) ? `'${raw}` : raw;
  return `"${safe.replace(/"/g, '""')}"`;
}

/** Export the exact loaded aggregate scope, without a second request or raw events. */
export function productEngagementCsv(data: ProductEngagement): string {
  const rows: Row[] = [
    { section: 'period_start', date: data.period.startDate }, { section: 'period_end', date: data.period.endDate },
    { section: 'timezone', name: data.period.timeZone }, { section: 'scope', name: data.scope },
    { section: 'generated_at', date: data.generatedAt },
    { section: 'sessions', sessions: data.sessions.sessions, users: data.sessions.users, durationMs: data.sessions.durationMs, avgDurationMs: data.sessions.avgSessionMs },
    { section: 'avg_duration_per_user', avgDurationMs: data.sessions.avgUserMs },
    ...data.events.map(row => ({ section: 'event_total', ...row })),
    ...data.daily.flatMap(day => [
      { section: 'daily_sessions', date: day.date, sessions: day.sessions, durationMs: day.durationMs },
      ...Object.entries(day.events).map(([event, count]) => ({ section: 'daily_event', date: day.date, event, count })),
    ]),
    ...data.breakdowns.map(row => ({ section: 'breakdown', ...row })),
    ...data.lenses.flatMap(row => [
      { section: 'top_50_lens_users', id: row.extensionId, name: row.name, users: row.users },
      { section: 'top_50_lenses', id: row.extensionId, name: row.name, event: 'lens_draft_saved', count: row.drafts },
      { section: 'top_50_lenses', id: row.extensionId, name: row.name, event: 'lens_published', count: row.publishes },
      { section: 'top_50_lenses', id: row.extensionId, name: row.name, event: 'lens_try', count: row.tries },
    ]),
    ...data.topUsers.map(row => ({ section: 'top_50_users', id: row.userId, name: row.name, sessions: row.sessions, durationMs: row.durationMs, avgDurationMs: row.durationMs / row.sessions })),
    ...data.notes.map(name => ({ section: 'note', name })),
    { section: 'note', name: 'top_50_lens_users is distinct users across all three Lens actions, not distinct users per event.' },
    ...(data.breakdownsTruncated ? [{ section: 'note', name: 'Breakdown export limited to the top 500 rows. Event totals and daily counts are complete for the retained scope.' }] : []),
    ...(data.retentionLimited ? [{ section: 'note', name: 'The selected dates exceed the two-year product event retention window.' }] : []),
  ];
  return '\uFEFF' + [columns.map(cell).join(','), ...rows.map(row => columns.map(column => cell(row[column])).join(','))].join('\r\n') + '\r\n';
}

export function downloadProductEngagement(data: ProductEngagement): void {
  const url = URL.createObjectURL(new Blob([productEngagementCsv(data)], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `product-engagement-${data.period.startDate}-${data.period.endDate}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
